"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useUi } from "@/components/ui/use-ui";
import { cn } from "@/lib/cn";

/**
 * Horizontal row that bleeds to the viewport edge like beeg's strips,
 * with hover arrows on desktop and native swipe on touch.
 */
export function HScroll({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const t = useUi();

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdges({ start: el.scrollLeft > 4, end: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [update]);

  const scroll = (dir: 1 | -1) => {
    const el = ref.current;
    el?.scrollBy({ left: dir * el.clientWidth * 0.75, behavior: "smooth" });
  };

  const arrow = (dir: 1 | -1) => (
    <button
      type="button"
      aria-label={dir < 0 ? t.common.scrollLeft : t.common.scrollRight}
      onClick={() => scroll(dir)}
      className={cn(
        "absolute inset-y-0 z-10 hidden w-16 items-center opacity-0 transition-opacity duration-200 group-hover/hs:opacity-100 md:flex",
        dir < 0 ? "left-0 justify-start bg-linear-to-r pl-2" : "right-0 justify-end bg-linear-to-l pr-2",
        "from-ink via-ink/70 to-transparent",
      )}
    >
      <span className="glass grid size-9 place-items-center rounded-full text-fg shadow-lg hover:bg-white/15">
        {dir < 0 ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
      </span>
    </button>
  );

  return (
    <div className="group/hs relative -mx-3 md:-mx-5">
      <div ref={ref} onScroll={update} className={cn("no-scrollbar flex overflow-x-auto overscroll-x-contain px-3 md:px-5", className)}>
        {children}
      </div>
      {edges.start && arrow(-1)}
      {edges.end && arrow(1)}
    </div>
  );
}
